import { createContext, useState, useEffect, useContext, ReactNode } from 'react';

export interface Article {
  id: string;
  userId: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  featured: boolean;
  createdAt: string;
  updatedAt: string;
  imageUrl?: string;
  featureImageUrl?: string;
  thumbnailImageUrl?: string;
  articleImageUrls?: string[];
}

interface ArticleContextType {
  articles: Article[];
  addArticle: (article: Article) => void;
  loading: boolean;
}

const ArticleContext = createContext<ArticleContextType | undefined>(undefined);

export const ArticleProvider = ({ children }: { children: ReactNode }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch('/api/articles');
        if (!response.ok) {
          throw new Error('Failed to fetch articles');
        }
        const data = await response.json();
        setArticles(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const addArticle = (article: Article) => {
    setArticles(prevArticles => [article, ...prevArticles]);
  };

  return (
    <ArticleContext.Provider value={{ articles, addArticle, loading }}>
      {children}
    </ArticleContext.Provider>
  );
};

export const useArticles = () => {
  const context = useContext(ArticleContext);
  if (context === undefined) {
    throw new Error('useArticles must be used within an ArticleProvider');
  }
  return context;
};
